import { Box, Card, Typography, alpha, useTheme, IconButton, Tooltip, Stack } from "@mui/material";
import { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { LuUser, LuBuilding, LuServer, LuCalendar } from "react-icons/lu";
import { TbActivity, TbBolt, TbRefresh } from "react-icons/tb";
import { useAuth } from "../../authentication/AuthProvider";
import dayjs from "dayjs";
import UsageTabs from "./UsageTabs";
import UsageAllCompany from "./UsageAllCompany";
import UsageCompany from "./UsageCompany";
import UsageMine from "./UsageMine";
import DateSelector from "../../components/input/DateSelector";
import SearchBox from "../../components/input/SearchBox";
import { getOrgTokens, ORG_PALETTE } from "../../theme/style/organizationPalette";

const SummaryCard = ({ title, value, color, icon: Icon }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const tok = getOrgTokens(isDark);

  return (
    <Card sx={{
      p: 2.5,
      borderRadius: tok.radius.card,
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      flex: 1,
      minWidth: '200px',
      boxShadow: tok.shadow.card,
      border: '1px solid',
      borderColor: tok.border.card,
      background: tok.bg.card,
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: tok.shadow.cardHover,
        borderColor: alpha(color, 0.3)
      }
    }}>
      <Box sx={{
        p: 1.2,
        borderRadius: tok.radius.tabItem,
        bgcolor: alpha(color, 0.1),
        color: color,
        display: 'flex',
        boxShadow: tok.shadow.iconBox
      }}>
        <Icon size={20} />
      </Box>
      <Box>
        <Typography sx={{ 
          color: tok.text.muted, 
          fontWeight: 600, 
          fontSize: '11px',
          letterSpacing: '0.02em',
          textTransform: 'none',
          fontFamily: tok.font.family,
          mb: 0.5
        }}>
          {title}
        </Typography>
        <Typography sx={{
          fontWeight: 500,
          fontSize: tok.font.size.metricValueSm || '20px',
          color: tok.text.primary,
          fontFamily: tok.font.family
        }}>
          {value}
        </Typography>
      </Box>
    </Card>
  );
};

const UsageDashboard = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const tok = getOrgTokens(isDark);
  const mc = ORG_PALETTE.metricCards;
  const { t } = useTranslation();
  const { isAdmin, isCompanyAdmin } = useAuth();

  const [selectedTab, setSelectedTab] = useState("Mine");
  const [startDate, setStartDate] = useState(dayjs().startOf("month"));
  const [endDate, setEndDate] = useState(dayjs());
  const [searchText, setSearchText] = useState("");
  const [refresh, setRefresh] = useState(false);
  const [stats, setStats] = useState({ capacity: '—', modules: '—', sync: '—', totalEntries: 0 });

  const tabs = useMemo(() => [
    { label: "My Usage", value: "Mine", tabIcon: LuUser },
    ...(isCompanyAdmin ? [{ label: "Company Usage", value: "My Company", tabIcon: LuBuilding }] : []),
    ...(isAdmin ? [{ label: "Platform Usage", value: "All Companies", tabIcon: LuServer }] : [])
  ], [isCompanyAdmin, isAdmin]);

  return (
    <Box sx={{
      width: "100%",
      animation: "fadeIn 0.5s ease-out",
      p: 0,
      minHeight: '100vh',
      bgcolor: 'transparent',
    }}>
      {/* 1. Page Title */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ 
          fontWeight: tok.font.weight.bold, 
          display: "flex", 
          alignItems: "center", 
          gap: 2, 
          color: tok.text.primary, 
          fontFamily: tok.font.family,
          textTransform: 'none'
        }}>
          <Box sx={{
            p: 1.2,
            borderRadius: tok.radius.icon || 2.5,
            bgcolor: alpha(tok.bg.tabActive, 0.1),
            color: tok.bg.tabActive,
            display: "flex",
            boxShadow: tok.shadow.iconBox
          }}>
            <LuUser size={22} />
          </Box>
          My Usage
        </Typography>
      </Box>

      <Box sx={{ px: { xs: 2, md: 3 }, pb: 4 }}>
        <Stack spacing={4}>
          {/* 2. Primary Tabs */}
          <Box sx={{ width: '100%' }}>
            <UsageTabs selected={selectedTab} setSelected={setSelectedTab} options={tabs} />
          </Box>

          {/* 3. Filter and Stats Bar */}
          <Box sx={{
            px: 2.5, py: 2, borderRadius: tok.radius.card, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            border: '1px solid', borderColor: tok.border.card,
            background: tok.bg.card,
            width: '100%',
            boxShadow: tok.shadow.card,
            flexWrap: 'wrap',
            gap: 3
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexShrink: 0 }}>
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: 2,
                px: 2, py: 0.8, borderRadius: tok.radius.tabItem, 
                border: '1px solid', borderColor: tok.border.card,
                bgcolor: tok.bg.page
              }}>
                <Box sx={{ color: mc.scope2.accent, display: 'flex' }}><LuCalendar size={18} /></Box>
                <Box sx={{ width: '130px' }}><DateSelector label="" value={startDate} onChange={setStartDate} /></Box>
                <Box sx={{ width: 8, height: '2px', bgcolor: tok.border.card, borderRadius: 1 }} />
                <Box sx={{ width: '130px' }}><DateSelector label="" value={endDate} onChange={setEndDate} /></Box>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: '60px', ml: 1 }}>
                <Typography sx={{ 
                  color: tok.text.primary, 
                  fontWeight: 800, 
                  lineHeight: 1.1, 
                  fontSize: '20px',
                  fontFamily: tok.font.family,
                  textTransform: 'none'
                }}>
                  {stats.totalEntries.toLocaleString()}
                </Typography>
                <Typography sx={{ 
                  color: tok.text.muted, 
                  fontWeight: 700, 
                  fontSize: '10px', 
                  opacity: 0.8,
                  fontFamily: tok.font.family,
                  textTransform: 'none',
                  letterSpacing: '0.5px'
                }}>
                  Analytical data points
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexGrow: 1, justifyContent: 'flex-end', minWidth: 0 }}>
              <Box sx={{ width: '100%', maxWidth: '260px' }}>
                <SearchBox text={searchText} setText={setSearchText} />
              </Box>
              <Tooltip title="Sync Data">
                <IconButton onClick={() => setRefresh(!refresh)} sx={{
                  width: 42,
                  height: 42,
                  border: '1px solid',
                  borderColor: tok.border.card,
                  borderRadius: tok.radius.tabItem,
                  bgcolor: tok.bg.card,
                  transition: 'all 0.2s ease',
                  '&:hover': { color: tok.bg.tabActive, bgcolor: alpha(tok.bg.tabActive, 0.05), borderColor: alpha(tok.bg.tabActive, 0.2) }
                }}>
                  <TbRefresh size={20} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* 4. Tab Content */}
          <Box sx={{ minHeight: "600px" }}>
            {selectedTab === "Mine" && (
              <UsageMine
                startDate={startDate}
                endDate={endDate}
                setStartDate={setStartDate}
                setEndDate={setEndDate}
                searchText={searchText}
                refresh={refresh}
                setGlobalStats={setStats}
              />
            )}
            {selectedTab === "My Company" && (
              <UsageCompany
                startDate={startDate}
                endDate={endDate}
                setStartDate={setStartDate}
                setEndDate={setEndDate}
                searchText={searchText}
                refresh={refresh}
                setGlobalStats={setStats}
              />
            )}
            {selectedTab === "All Companies" && (
              <UsageAllCompany
                startDate={startDate}
                endDate={endDate}
                setStartDate={setStartDate}
                setEndDate={setEndDate}
                searchText={searchText}
                refresh={refresh}
                setGlobalStats={setStats}
              />
            )}
          </Box>
        </Stack>
      </Box>
    </Box>
  );
};

export default UsageDashboard;
